import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const UploadTrackingCtx = createContext(null);

export function useUploadTracking() {
  return useContext(UploadTrackingCtx);
}

export function UploadTrackingProvider({ children }) {
  const [activeUpload, setActiveUpload] = useState(null); // { processId, filename, supplierName, fileDescription, referenceDate }
  const [pendingResult, setPendingResult] = useState(null); // result data to show in modal
  const location = useLocation();
  const intervalRef = useRef(null);

  const startTracking = useCallback((info) => {
    setActiveUpload(info);
    setPendingResult(null);
  }, []);

  const clearResult = useCallback(() => {
    setPendingResult(null);
  }, []);

  // Poll for active upload status
  useEffect(() => {
    if (!activeUpload) return;

    const apiBase = import.meta.env.VITE_API_URL || '';
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const res = await fetch(`${apiBase}/api/upload-products/status/${activeUpload.processId}`);
        const data = await res.json();

        if (stopped) return;

        if (data.status === 'done' || data.status === 'failed') {
          setActiveUpload(null);
          // If user is on /supplier-products, show result modal
          if (location.pathname === '/supplier-products') {
            setPendingResult({
              ...data,
              filename: activeUpload.filename,
              supplierName: activeUpload.supplierName,
              fileDescription: activeUpload.fileDescription,
              referenceDate: activeUpload.referenceDate,
            });
          }
          // Backend already creates notification for bell icon
        }
      } catch {
        // silent
      }
    };

    intervalRef.current = setInterval(poll, 2500);
    poll(); // immediate first poll

    return () => {
      stopped = true;
      clearInterval(intervalRef.current);
    };
  }, [activeUpload, location.pathname]);

  return (
    <UploadTrackingCtx.Provider value={{ activeUpload, startTracking, pendingResult, clearResult }}>
      {children}
    </UploadTrackingCtx.Provider>
  );
}
